// The identity a payer DECLARES on the two non-searchable school-fee journeys,
// and the one place that decides whether a declaration is complete.
//
// ⛔ The journey context deliberately does NOT gate this. `canEnter` treats a
// placement or a matricule as "identified", which is loose because the two
// journeys disagree about which fields even exist -- registration has no
// matricule at all. The FORM is what must refuse an incomplete declaration,
// and this is what it refuses on.
//
// ⚠ It is a declaration, not an authentication. Nothing here proves the payer
// knows the student; the server records the values as payer-asserted.

export type DeclaredIdentity = {
  matricule: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
};

// A native <input type="date"> only ever yields this shape or "", but the same
// value also arrives from journey context, which an earlier step wrote.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// Today in the browser's own calendar, as the date control's ceiling. Built
// from the local parts rather than toISOString(), which converts to UTC first
// and so names yesterday for anyone east of Greenwich in the early hours.
export const todayIso = (now: Date = new Date()): string => {
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");

  return `${now.getFullYear()}-${month}-${day}`;
};

/**
 * A real calendar date, in the canonical form the pay contract carries, and
 * not in the future.
 *
 * ⛔ The round-trip comparison is the point: `new Date("2026-02-31")` does not
 * throw, it rolls forward to 3 March. Accepting the parse alone would send a
 * date the payer never typed, on the field the school uses to tell two pupils
 * with the same name apart.
 */
export const isValidDateOfBirth = (
  value: string,
  now: Date = new Date()
): boolean => {
  const trimmed = value.trim();

  if (!ISO_DATE.test(trimmed)) return false;

  const parsed = new Date(`${trimmed}T00:00:00Z`);

  if (Number.isNaN(parsed.getTime())) return false;
  if (parsed.toISOString().slice(0, 10) !== trimmed) return false;

  return trimmed <= todayIso(now);
};

// Whether the declaration may be submitted. Matricule is required on the
// manual journey and MUST NOT exist on the registration one, so the caller
// says which it is rather than this inferring it from an empty string.
export const isCompleteIdentity = (
  identity: DeclaredIdentity,
  { requiresMatricule }: { requiresMatricule: boolean },
  now: Date = new Date()
): boolean =>
  (!requiresMatricule || identity.matricule.trim() !== "") &&
  identity.firstName.trim() !== "" &&
  identity.lastName.trim() !== "" &&
  isValidDateOfBirth(identity.dateOfBirth, now);
